import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Zap, Shield, Crown, TrendingUp, DollarSign, Clock,
  CheckCircle, ArrowRight, Star, Sparkles, CreditCard,
  BadgeCheck, Bell, BarChart3, Users, Truck
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PremiumFeaturesPage = () => {
  const [activeTab, setActiveTab] = useState('quickpay');
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Quick Pay state
  const [quickPayHistory, setQuickPayHistory] = useState([]);
  const [transactionId, setTransactionId] = useState('');
  const [driverEmail, setDriverEmail] = useState('');
  
  // Insurance state
  const [insuranceRates, setInsuranceRates] = useState(null);
  const [insurancePolicies, setInsurancePolicies] = useState([]);
  
  // Subscription state
  const [tiers, setTiers] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [revenueRes, ratesRes, tiersRes] = await Promise.all([
        axios.get(`${API}/admin/revenue/dashboard`),
        axios.get(`${API}/insurance/rates`),
        axios.get(`${API}/premium/tiers`)
      ]);
      
      setRevenue(revenueRes.data);
      setInsuranceRates(ratesRes.data.rates);
      setTiers(tiersRes.data.tiers);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPayRequest = async () => {
    if (!transactionId || !driverEmail) {
      toast.error('Please enter transaction ID and driver email');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/quickpay/request`, {
        transaction_id: transactionId,
        driver_email: driverEmail,
        payment_method: 'bank_transfer'
      });
      
      toast.success(`Quick Pay requested! $${res.data.payout_amount} will be sent within 2 hours`);
      setTransactionId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Quick Pay request failed');
    }
  };

  const tabs = [
    { id: 'quickpay', label: 'Quick Pay', icon: Zap },
    { id: 'insurance', label: 'Insurance', icon: Shield },
    { id: 'subscriptions', label: 'Premium Plans', icon: Crown },
    { id: 'revenue', label: 'Revenue', icon: TrendingUp }
  ];

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6" data-testid="premium-features-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-amber-500" />
            Premium Features & Revenue
          </h1>
          <p className="text-gray-400 mt-1">Quick Pay, Insurance Products, and Premium Subscriptions</p>
        </div>

        {/* Revenue Summary Cards */}
        {revenue && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 text-sm font-medium">Total Revenue</p>
                    <p className="text-2xl font-bold text-white font-mono">${revenue.total_revenue.toLocaleString()}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Transaction Fees</p>
                    <p className="text-xl font-bold text-white font-mono">${revenue.breakdown.transaction_fees.total.toLocaleString()}</p>
                  </div>
                  <p className="text-blue-400 text-sm">{revenue.breakdown.transaction_fees.percentage}%</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Quick Pay Fees</p>
                    <p className="text-xl font-bold text-white font-mono">${revenue.breakdown.quick_pay_fees.total.toLocaleString()}</p>
                  </div>
                  <Zap className="w-6 h-6 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">Insurance</p>
                    <p className="text-xl font-bold text-white font-mono">${revenue.breakdown.insurance_premiums.total.toLocaleString()}</p>
                  </div>
                  <Shield className="w-6 h-6 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Quick Pay Tab */}
        {activeTab === 'quickpay' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Quick Pay - Instant Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 font-medium mb-2">How it works:</p>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Drivers get paid within 2 hours instead of 30 days</li>
                    <li>• <span className="text-white font-medium">2.5% fee</span> deducted from payout</li>
                    <li>• Supports bank transfer, Zelle, Venmo</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Transaction ID</Label>
                    <Input
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Enter transaction ID"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      data-testid="quickpay-transaction-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Driver Email</Label>
                    <Input
                      value={driverEmail}
                      onChange={(e) => setDriverEmail(e.target.value)}
                      placeholder="driver@example.com"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      data-testid="quickpay-email-input"
                    />
                  </div>
                  <Button 
                    onClick={handleQuickPayRequest}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                    data-testid="quickpay-submit-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" /> Process Quick Pay
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  Quick Pay Calculator
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1500, 3000, 5000, 10000].map(amount => {
                    const fee = amount * 0.025;
                    const payout = amount - fee; // Tempus does NOT take a cut of freight; Quick Pay is a finance partner fee only
                    return (
                      <div key={amount} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div>
                          <p className="text-white font-medium">${amount.toLocaleString()} Load</p>
                          <p className="text-xs text-gray-500">Carrier-financed early-pay (via factoring partner)</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-amber-400">-${fee.toFixed(2)} Quick Pay fee</p>
                          <p className="text-lg font-bold text-green-400">${payout.toFixed(2)} payout</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Insurance Tab */}
        {activeTab === 'insurance' && insuranceRates && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(insuranceRates).map(([key, rate]) => (
                <Card key={key} className="bg-slate-800/50 border-slate-700 hover:border-purple-500/50 transition-colors">
                  <CardContent className="p-6">
                    <Shield className={`w-8 h-8 mb-4 ${
                      key === 'basic' ? 'text-blue-400' :
                      key === 'standard' ? 'text-green-400' :
                      key === 'premium' ? 'text-purple-400' :
                      'text-orange-400'
                    }`} />
                    <h3 className="text-lg font-bold text-white mb-2">{rate.name}</h3>
                    
                    {rate.rate_per_mile ? (
                      <div className="mb-4">
                        <p className="text-2xl font-bold text-white">${rate.rate_per_mile}/mi</p>
                        <p className="text-sm text-gray-400">Up to ${rate.max_coverage.toLocaleString()} coverage</p>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <p className="text-2xl font-bold text-white">${rate.flat_rate}</p>
                        <p className="text-sm text-gray-400">${(rate.coverage / 1000000).toFixed(0)}M liability coverage</p>
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => toast.info('Insurance purchase available when booking loads')}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Shield className="w-10 h-10 text-purple-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Per-Load Insurance Integration</h3>
                    <p className="text-gray-400 mb-4">
                      Insurance options are automatically offered when carriers bid on loads. 
                      Carriers can add coverage at checkout, and premiums are collected with the transaction fee.
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" /> Instant coverage
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" /> No paperwork
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" /> Claims support
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Premium Plans Tab */}
        {activeTab === 'subscriptions' && tiers && (
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(tiers).map(([key, tier]) => (
              <Card 
                key={key} 
                className={`relative overflow-hidden ${
                  key === 'pro' 
                    ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500/50' 
                    : 'bg-slate-800/50 border-slate-700'
                }`}
              >
                {key === 'pro' && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="mb-6">
                    {key === 'free' && <Users className="w-10 h-10 text-gray-400 mb-3" />}
                    {key === 'pro' && <Star className="w-10 h-10 text-blue-400 mb-3" />}
                    {key === 'enterprise' && <Crown className="w-10 h-10 text-amber-400 mb-3" />}
                    
                    <h3 className="text-xl font-bold text-white capitalize">{key}</h3>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-3xl font-black text-white">${tier.price}</span>
                      {tier.price > 0 && <span className="text-gray-400">/month</span>}
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          key === 'pro' ? 'text-blue-400' : 
                          key === 'enterprise' ? 'text-amber-400' : 
                          'text-gray-500'
                        }`} />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${
                      key === 'pro' 
                        ? 'bg-blue-600 hover:bg-blue-500' 
                        : key === 'enterprise'
                        ? 'bg-amber-500 hover:bg-amber-400 text-black'
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    onClick={() => toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} plan selected!`)}
                    data-testid={`subscribe-${key}-btn`}
                  >
                    {tier.price === 0 ? 'Current Plan' : 'Upgrade Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && revenue && (
          <div className="space-y-6">
            {/* Revenue Breakdown */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Revenue Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: 'Software Subscriptions', data: revenue.breakdown.transaction_fees, color: 'bg-blue-500' },
                    { name: 'Quick Pay Partner Fees (2.5%)', data: revenue.breakdown.quick_pay_fees, color: 'bg-amber-500' },
                    { name: 'Insurance Partner Referrals', data: revenue.breakdown.insurance_premiums, color: 'bg-purple-500' },
                    { name: 'Premium Tiers', data: { total: revenue.breakdown.subscriptions.monthly, count: revenue.breakdown.subscriptions.active_subscribers, percentage: revenue.breakdown.subscriptions.percentage }, color: 'bg-green-500' }
                  ].map((item, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-300">{item.name}</span>
                        <span className="text-white font-bold font-mono">${item.data.total.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${item.color} rounded-full`}
                            style={{ width: `${Math.max(item.data.percentage, 2)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-12">{item.data.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Growth Opportunities */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Growth Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {revenue.growth_opportunities.map((opp, idx) => (
                    <div key={idx} className="p-4 bg-slate-700/30 rounded-lg">
                      <h4 className="font-medium text-white mb-2">{opp.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <span>Current: {opp.current}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span>Potential: {opp.potential}</span>
                      </div>
                      <p className="text-green-400 font-bold">
                        +${opp.revenue_potential.toLocaleString()} potential
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PremiumFeaturesPage;
